import { useTranslation } from 'react-i18next';
import { Clock, User, FileText, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { ChangelogEntry } from '../types';

interface SiteChangelogProps {
  changelog: ChangelogEntry[];
  isLoading?: boolean;
  className?: string;
}

function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

function formatUserName(user: { firstname?: string; lastname?: string } | null): string {
  if (!user) return 'Unknown user';
  const parts = [user.firstname, user.lastname].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : 'Unknown user';
}

function formatFieldName(field: string): string {
  const fieldMappings: Record<string, string> = {
    name: 'Name',
    abbreviations: 'Abbreviations',
    description: 'Description',
    phone: 'Phone',
    extension: 'Extension',
    phoneExtension: 'Extension',
    fax: 'Fax',
    fax_extension: 'Fax Extension',
    faxExtension: 'Fax Extension',
    email: 'Email',
    address: 'Address',
    addressLine1: 'Address',
    city: 'City',
    postcode: 'Postal Code',
    postalCode: 'Postal Code',
    state_province_county: 'Province',
    stateProvince: 'Province',
    country: 'Country',
    disabled: 'Status',
    tags: 'Tags',
    latitude: 'Latitude',
    longitude: 'Longitude',
    geolocation: 'Geolocation',
    locations: 'Locations',
    usergroups: 'User Groups',
  };

  if (fieldMappings[field]) return fieldMappings[field];

  return field
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

function formatFieldValue(value: unknown): string {
  if (value === null || value === undefined) return '(empty)';
  if (typeof value === 'boolean') return value ? 'Active' : 'Inactive';
  if (Array.isArray(value)) {
    if (value.length === 0) return '(none)';
    return value.join(', ');
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function ChangeItem({ field, oldValue, newValue }: { field: string; oldValue?: unknown; newValue?: unknown }) {
  const fieldName = formatFieldName(field);
  const oldDisplay = formatFieldValue(oldValue);
  const newDisplay = formatFieldValue(newValue);

  return (
    <div className="text-sm py-1">
      <span className="font-medium text-foreground">{fieldName}:</span>{' '}
      {oldValue !== undefined && (
        <>
          <span className="text-red-600 line-through">{oldDisplay}</span>{' '}
        </>
      )}
      {newValue !== undefined && (
        <span className="text-green-600">{newDisplay}</span>
      )}
    </div>
  );
}

function ChangelogItem({ entry, isFirst }: { entry: ChangelogEntry; isFirst: boolean }) {
  const { t } = useTranslation('sites');

  return (
    <div className={cn('relative pl-6 pb-6', !isFirst && 'pt-0')}>
      <div className="absolute left-[9px] top-6 bottom-0 w-0.5 bg-border" />
      <div
        className={cn(
          'absolute left-0 top-1 w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center',
          entry.isOriginal
            ? 'bg-primary border-primary'
            : 'bg-background border-border'
        )}
      >
        {entry.isOriginal ? (
          <FileText className="h-2.5 w-2.5 text-primary-foreground" />
        ) : (
          <div className="w-2 h-2 rounded-full bg-border" />
        )}
      </div>

      <div className="bg-muted/30 rounded-lg p-3">
        <div className="flex items-center gap-4 text-xs text-muted-foreground mb-2">
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            <span>{formatTimestamp(entry.timestamp)}</span>
          </div>
          <div className="flex items-center gap-1">
            <User className="h-3 w-3" />
            <span>{formatUserName(entry.user)}</span>
          </div>
        </div>

        {entry.isOriginal ? (
          <div className="text-sm text-muted-foreground italic">
            {t('changelog.created', 'Created')}
          </div>
        ) : entry.changes && entry.changes.length > 0 ? (
          <div className="space-y-0.5">
            {entry.changes.map((change, index) => (
              <ChangeItem
                key={`${change.field}-${index}`}
                field={change.field}
                oldValue={change.old}
                newValue={change.new}
              />
            ))}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground italic">
            {t('changelog.updated', 'Updated')}
          </div>
        )}
      </div>
    </div>
  );
}

export function SiteChangelog({ changelog, isLoading = false, className }: SiteChangelogProps) {
  const { t } = useTranslation('sites');

  if (isLoading) {
    return (
      <div className={cn('flex items-center justify-center py-12', className)}>
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (changelog.length === 0) {
    return (
      <div className={cn('flex flex-col items-center justify-center py-12 text-muted-foreground', className)}>
        <FileText className="h-12 w-12 mb-2" />
        <span>{t('changelog.empty', 'No history available')}</span>
      </div>
    );
  }

  return (
    <ScrollArea className={cn('h-[400px] pr-4', className)}>
      <div className="space-y-0">
        {changelog.map((entry, index) => (
          <ChangelogItem
            key={entry._id || index}
            entry={entry}
            isFirst={index === 0}
          />
        ))}
      </div>
    </ScrollArea>
  );
}
